// frontend/src/pages/community/CommunityPage.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { 
  MessageSquare, 
  Users, 
  Search, 
  Plus, 
  Home,
  Bell,
  Filter,
  RefreshCw,
  X,
  ShoppingCart,
  Store,
  ShoppingBag,
  Heart,
  Flag,
  User
} from 'lucide-react';
import PostCard from '../../components/community/PostCard';
import CreatePostModal from '../../components/community/CreatePostModal';
import MessagesPanel from '../../components/community/MessagesPanel';
import ConversationList from '../../components/community/ConversationList';
import NetworkTab from '../../components/community/NetworkTab';
import NotificationsTab from '../../components/community/NotificationsTab';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';
import userStatsService from '../../services/userStats.service';
import ReportModal from '../../components/community/ReportModal';
import useNotificationsStore from '../../store/notificationsStore';
import { toast } from 'react-hot-toast';
import communityAPI from '../../services/community.api';

const CommunityPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [feedType, setFeedType] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const { getUnreadCount, unreadCount } = useNotificationsStore();
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [userStats, setUserStats] = useState({
    postsCount: 0,
    followersCount: 0,
    followingCount: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  
  const {
    feed,
    feedPagination,
    isLoading,
    activeTab,
    showCreatePostModal,
    searchResults,
    searchUsers,
    searchPosts,
    getFeed,
    setActiveTab,
    setShowCreatePostModal,
    clearSearchResults,
    getConversations,
    conversations,
    unreadMessageCount,
    getUnreadMessageCount,
  } = useCommunityStore();
  
  const { user } = useAuthStore();

  // Set default tab to feed when component mounts
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (!tabParam || !['feed', 'messages', 'network', 'notifications'].includes(tabParam)) {
      setActiveTab('feed');
    }
  }, []);

  // Load unread message count on mount and set up periodic refresh
  useEffect(() => {
    getUnreadMessageCount();
    const interval = setInterval(() => {
      if (activeTab !== 'messages') {
        getUnreadMessageCount();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, getUnreadMessageCount]);

  // Refresh when returning to community page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        getUnreadMessageCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [getUnreadMessageCount]);

  // Handle URL parameters
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const userId = searchParams.get('user');
    
    if (tabParam && ['feed', 'messages', 'network', 'notifications'].includes(tabParam)) {
      setActiveTab(tabParam);
      if (tabParam === 'messages' && userId) {
        setSelectedUserId(userId);
        toast.success(`Opening chat...`, { duration: 2000 });
      }
    }
  }, [searchParams, setActiveTab]);

  // Update URL when active tab changes
  useEffect(() => {
    if (activeTab) {
      const params = new URLSearchParams(searchParams);
      params.set('tab', activeTab);
      if (activeTab !== 'messages') params.delete('user');
      setSearchParams(params);
    }
  }, [activeTab, searchParams, setSearchParams]);

  const hasLoadedConversations = useRef(false);
  useEffect(() => {
    if (activeTab === 'messages' && !hasLoadedConversations.current) {
      hasLoadedConversations.current = true;
      getConversations();
    } else if (activeTab !== 'messages') {
      hasLoadedConversations.current = false;
    }
  }, [activeTab, getConversations]);

  // Handle user selection from URL
  useEffect(() => {
    const selectUserFromUrl = async () => {
      if (selectedUserId && activeTab === 'messages') {
        if (isLoadingProfile) return;
        setIsLoadingProfile(true);
        
        if (conversations.length > 0) {
          const existingConversation = conversations.find(conv => conv.user?.id === selectedUserId);
          if (existingConversation) {
            setSelectedConversation(existingConversation);
            setSelectedUserId(null);
            setIsLoadingProfile(false);
            return;
          }
        }
        try {
          const response = await communityAPI.getUserProfile(selectedUserId);
          if (response.success) {
            const tempConversation = {
              id: `temp_${selectedUserId}`,
              type: 'personal',
              user: {
                id: response.data.id,
                firstName: response.data.firstName,
                lastName: response.data.lastName,
                avatar: response.data.avatar,
                businessTags: response.data.businessTags
              },
              lastMessage: null,
              lastMessageAt: new Date().toISOString(),
              unreadCount: 0,
              businessContext: null
            };
            setSelectedConversation(tempConversation);
            toast.success(`Starting chat with ${tempConversation.user.firstName}`, { duration: 3000 });
          }
        } catch (error) {
          console.error('Failed to fetch user details:', error);
          toast.error('Could not load user details');
        } finally {
          setSelectedUserId(null);
          setIsLoadingProfile(false);
        }
      }
    };
    
    const timer = setTimeout(() => {
      selectUserFromUrl();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [selectedUserId, conversations, activeTab]);

  useEffect(() => {
    loadFeed();
    loadStats();
  }, [feedType]);

  useEffect(() => {
    if (activeTab === 'feed') loadStats();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'notifications') getUnreadCount();
  }, [activeTab, getUnreadCount]);

  const loadFeed = async () => {
    try {
      await getFeed({ following: feedType === 'following', page: 1, limit: 20 });
    } catch (error) {
      console.error('Failed to load feed:', error);
    }
  };

  const loadStats = async () => {
    setIsLoadingStats(true);
    try {
      const stats = await userStatsService.getMyStats();
      setUserStats(stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      try {
        setShowSearchResults(true);
        if (activeTab === 'feed') await searchPosts(searchQuery.trim());
        else if (activeTab === 'network') await searchUsers(searchQuery.trim());
        else {
          await searchUsers(searchQuery.trim());
          await searchPosts(searchQuery.trim());
        }
      } catch (error) {
        console.error('Search error:', error);
        toast.error('Failed to perform search');
      }
    }
  };

  const handleCreatePost = () => setShowCreatePostModal(true);
  const handlePostCreated = () => { userStatsService.incrementPostCount(); loadStats(); };
  const handlePostDeleted = () => { userStatsService.decrementPostCount(); loadStats(); };

  const tabs = [
    { id: 'feed', name: 'Feed', icon: Home, color: 'primary' },
    { id: 'messages', name: 'Messages', icon: MessageSquare, color: 'blue', badge: unreadMessageCount },
    { id: 'network', name: 'Network', icon: Users, color: 'purple' },
    { id: 'notifications', name: 'Notifications', icon: Bell, color: 'yellow', badge: unreadCount },
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId !== 'messages') setSelectedConversation(null);
    if (showSearchResults) setShowSearchResults(false);
    if (tabId === 'messages') getUnreadMessageCount();
  };

  const renderSearchResults = () => {
    const hasUsers = searchResults?.users?.length > 0;
    const hasPosts = searchResults?.posts?.length > 0;
    if (!hasUsers && !hasPosts) return null;
    
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl shadow-neomorph p-4 border border-primary-100/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-3" />
              <h2 className="text-lg font-bold text-gray-800">Search Results for "{searchQuery}"</h2>
            </div>
            <button onClick={() => { setShowSearchResults(false); setSearchQuery(''); clearSearchResults(); }} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
              <X className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        </div>
        
        {hasUsers && (
          <div className="bg-white rounded-xl shadow-neomorph p-4 border border-primary-100/30">
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
              <div className="p-1.5 bg-purple-50 rounded-lg mr-2"><Users className="h-4 w-4 text-purple-600" /></div> People
            </h3>
            <div className="space-y-2">
              {searchResults.users.slice(0, 5).map((user) => (
                <div key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-white transition-all">
                  <button onClick={() => navigate(`/community/profile/${user.id}`)} className="flex items-center space-x-3 flex-1 text-left">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center">
                      {user.avatar ? <img src={user.avatar} alt={user.firstName} className="h-10 w-10 rounded-lg object-cover" /> : <span className="text-white font-semibold">{user.firstName[0]}{user.lastName[0]}</span>}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">{user.firstName} {user.lastName}</h4>
                      <p className="text-xs text-gray-500">{user.businessTags?.[0] || 'No business tags'}</p>
                      <div className="flex items-center space-x-2 mt-1 text-xs text-gray-400">
                        <span>{user._count?.followers || 0} followers</span><span>•</span><span>{user._count?.posts || 0} posts</span>
                      </div>
                    </div>
                  </button>
                </div>
              ))}
              {searchResults.users.length > 5 && (
                <button onClick={() => { setActiveTab('network'); setShowSearchResults(false); }} className="w-full text-center py-2 text-primary-600 hover:text-primary-800 font-medium text-sm">
                  View all {searchResults.users.length} users →
                </button>
              )}
            </div>
          </div>
        )}
        
        {hasPosts && (
          <div className="bg-white rounded-xl shadow-neomorph p-4 border border-primary-100/30">
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
              <div className="p-1.5 bg-primary-50 rounded-lg mr-2"><Home className="h-4 w-4 text-primary-600" /></div> Posts
            </h3>
            <div className="space-y-3">
              {searchResults.posts.slice(0, 3).map((post) => (
                <PostCard key={post.id} post={post} onPostDeleted={handlePostDeleted} />
              ))}
              {searchResults.posts.length > 3 && (
                <button onClick={() => { setActiveTab('feed'); setShowSearchResults(false); }} className="w-full text-center py-2 text-primary-600 hover:text-primary-800 font-medium text-sm">
                  View all {searchResults.posts.length} posts →
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    if (showSearchResults && searchQuery.trim()) return renderSearchResults();
    switch (activeTab) {
      case 'feed':
        return (
          <div className="space-y-3">
            {/* Create Post Card - Compact */}
            <div className="bg-white rounded-xl shadow-neomorph p-2.5 border border-primary-100/30">
              <div className="flex items-start space-x-2">
                <button onClick={() => navigate(`/community/profile/${user?.id}`)} className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center flex-shrink-0">
                  {user?.avatar ? <img src={user.avatar} alt={user.firstName} className="h-9 w-9 rounded-xl object-cover" /> : <span className="text-white font-semibold text-xs">{user?.firstName?.[0]}{user?.lastName?.[0]}</span>}
                </button>
                <button onClick={handleCreatePost} className="flex-1 text-left p-2 bg-gray-50 rounded-lg shadow-neomorph-inset hover:bg-gray-100 transition-all">
                  <span className="text-xs text-gray-500">What's on your mind, {user?.firstName}?</span>
                </button>
              </div>
              <div className="mt-2 pt-2 border-t border-primary-100/50">
                <div className="flex items-center text-[10px] text-gray-600">
                  <Home className="h-3 w-3 text-primary-500 mr-1" />
                  <span className="text-[10px]">Your community stats: <span className="font-semibold text-primary-600">{userStats.postsCount} posts • {userStats.followersCount} followers • {userStats.followingCount} following</span></span>
                </div>
              </div>
            </div>

            {/* Feed Filters - Compact */}
            <div className="bg-white rounded-xl shadow-neomorph p-2 border border-primary-100/30">
              <div className="flex items-center justify-between">
                <div className="flex space-x-1.5">
                  <button onClick={() => setFeedType('all')} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${feedType === 'all' ? 'bg-primary-100 text-primary-700 shadow-neomorph-inset border border-primary-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>All</button>
                  <button onClick={() => setFeedType('following')} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${feedType === 'following' ? 'bg-primary-100 text-primary-700 shadow-neomorph-inset border border-primary-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Following ({userStats.followingCount})</button>
                </div>
                <button onClick={() => { loadFeed(); loadStats(); }} disabled={isLoading} className="p-1.5 bg-gray-100 rounded hover:bg-gray-200 transition-all disabled:opacity-50">
                  <RefreshCw className={`h-3.5 w-3.5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Posts Feed */}
            {feed.length > 0 ? (
              <div className="space-y-3">
                {feed.map((post) => <PostCard key={post.id} post={post} onPostDeleted={handlePostDeleted} />)}
                {feedPagination && feedPagination.totalPages > feedPagination.page && (
                  <div className="text-center mt-3">
                    <button onClick={() => getFeed({ page: feedPagination.page + 1, following: feedType === 'following' })} disabled={isLoading} className="px-4 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-sm disabled:opacity-50">
                      {isLoading ? 'Loading...' : 'Load More Posts'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-neomorph p-6 text-center border border-primary-100/30">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-50 shadow-neomorph-inset mb-2"><Home className="h-7 w-7 text-primary-400" /></div>
                <h3 className="text-base font-semibold text-gray-800 mb-1">{feedType === 'following' ? 'No posts from people you follow' : 'No posts yet'}</h3>
                <p className="text-sm text-gray-500 mb-3">{feedType === 'following' ? 'Start following people or encourage them to post!' : 'Be the first to share something with the community!'}</p>
                <button onClick={handleCreatePost} className="px-4 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg text-sm">Create First Post</button>
              </div>
            )}
          </div>
        );

      case 'messages':
        return (
          <div className="bg-white rounded-xl shadow-neomorph overflow-hidden border border-primary-100/30 h-[calc(100vh-200px)] min-h-[500px]">
            <div className="flex h-full">
              {/* Conversation List - takes 1/3 on desktop */}
              <div className={`${selectedConversation ? 'hidden lg:block lg:w-1/3' : 'w-full lg:w-1/3'} border-r border-gray-200`}>
                <ConversationList 
                  onSelectConversation={(conversation) => {
                    setSelectedConversation(conversation);
                    const params = new URLSearchParams(searchParams);
                    params.set('user', conversation.user.id);
                    if (conversation.businessContext) {
                      params.set('business', conversation.businessContext.id);
                    }
                    setSearchParams(params);
                  }} 
                  filterType={activeTab === 'messages' ? 'all' : undefined}
                />
              </div>
              
              {/* Messages Panel - takes 2/3 on desktop */}
              <div className={`${selectedConversation ? 'w-full lg:w-2/3' : 'hidden lg:block lg:w-2/3'}`}>
                {selectedConversation ? (
                  <MessagesPanel 
                    selectedConversation={selectedConversation}
                    onBack={() => {
                      setSelectedConversation(null);
                      const params = new URLSearchParams(searchParams);
                      params.delete('user');
                      params.delete('business');
                      setSearchParams(params);
                      getUnreadMessageCount();
                    }}
                  />
                ) : (
                  <div className="hidden lg:flex h-full items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">Select a conversation to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'network':
        return <NetworkTab />;
        
      case 'notifications':
        return <NotificationsTab />;
        
      default:
        return null;
    }
  };

  return (
    <div className="space-y-3">
      {/* Compact Header with Tabs */}
      <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
        {/* Top bar: Title + Actions + Search - Compact */}
        <div className="px-3 py-1.5 border-b border-primary-100/30">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center">
              <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5" />
              <h1 className="text-sm font-bold text-gray-800">FastConnect</h1>
              <p className="text-[10px] text-gray-500 ml-2 hidden sm:block">Connect with business professionals</p>
            </div>
            
            <div className="flex items-center space-x-1.5">
              {/* Search Bar - Compact */}
              <form onSubmit={handleSearch} className="relative">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                  <input 
                    type="search" 
                    value={searchQuery} 
                    onChange={(e) => { 
                      setSearchQuery(e.target.value); 
                      if (!e.target.value) { 
                        clearSearchResults(); 
                        setShowSearchResults(false); 
                      } 
                    }} 
                    onKeyDown={(e) => { 
                      if (e.key === 'Escape') { 
                        setSearchQuery(''); 
                        clearSearchResults(); 
                        setShowSearchResults(false); 
                      } 
                    }} 
                    placeholder="Search..." 
                    className="w-32 md:w-48 pl-6 pr-6 py-1 bg-white rounded shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs" 
                  />
                  {searchQuery && (
                    <button 
                      type="button" 
                      onClick={() => { 
                        setSearchQuery(''); 
                        clearSearchResults(); 
                        setShowSearchResults(false); 
                      }} 
                      className="absolute right-1.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
        
        {/* Bottom Tabs - Compact */}
        <div className="flex justify-around p-0.5">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button 
                key={tab.id} 
                onClick={() => handleTabClick(tab.id)} 
                className={`flex items-center px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  isActive 
                    ? `bg-${tab.color}-50 text-${tab.color}-600 shadow-neomorph-inset` 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon className={`h-3.5 w-3.5 mr-1 ${isActive ? `text-${tab.color}-600` : 'text-gray-500'}`} />
                <span className="hidden sm:inline">{tab.name}</span>
                {tab.badge > 0 && (
                  <span className={`ml-1 ${tab.id === 'notifications' ? 'bg-red-500 text-white' : `bg-${tab.color}-100 text-${tab.color}-600`} text-[9px] rounded-full px-1.5 py-0.5 font-medium`}>
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content - No extra wrapper card */}
      {renderTabContent()}

      {/* Business Tags (if any) */}
      {user?.businessTags && user.businessTags.length > 0 && (
        <div className="bg-white rounded-xl shadow-neomorph p-2.5 border border-primary-100/30">
          <h4 className="text-[10px] font-semibold text-gray-800 mb-1.5 flex items-center">
            <div className="w-1 h-3 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full mr-1" />
            Your Business Tags
          </h4>
          <div className="flex flex-wrap gap-1">
            {user.businessTags.map((tag, index) => (
              <span key={index} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-[10px] rounded border border-purple-200">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreatePostModal && <CreatePostModal onClose={() => setShowCreatePostModal(false)} onPostCreated={handlePostCreated} />}
      <ReportModal />
    </div>
  );
};

export default CommunityPage;